import { useReactFlow, NodeProps } from '@xyflow/react'
import {
  Search,
  Send,
  Brain,
  CheckCircle,
  RefreshCw
} from 'lucide-react'
import React, { memo, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface WebSearchQuery {
  id: string
  query: string
  selected: boolean
  reason: string
  disabled?: boolean
  status?: string
  nodeId?: string
  label?: string
}

interface ResearchItem {
  id: string
  query: string
  nodeLabel: string
  status: 'searching' | 'completed' | 'extracting' | 'creating_report' | 'report_completed' | 'report_error' | 'extraction_timeout'
  startedAt: string
  citations?: string[]
  citationCount?: number
  webExtractNodeId?: string
  webExtractNodeIds?: string[]
  documentNodeId?: string
  extractionProgress?: string
  completedAt?: string
  error?: string
}

interface NodeResearcherData {
  researchTopic?: string
  suggestedQueries?: WebSearchQuery[]
  researchList?: ResearchItem[]
  statusMessage?: string
  isLocked?: boolean
  isMinimized?: boolean
  assignedEmails?: string
  label?: string
  actionsTotal?: number
  documentRequest?: {
    'sysagent-cmd': string
    processing?: boolean
    timestamp?: number
    researchTopic?: string
    [key: string]: any
  }
}

const RESEARCH_EXAMPLES = [
  "Latest AI developments in 2025",
  "Climate change impact on agriculture",
  "Quantum computing breakthrough news",
  "Electric vehicle market trends",
  "Cybersecurity threats in healthcare"
]

const NodeResearcher: React.FC<NodeProps & { data: NodeResearcherData }> = ({
  id,
  data,
  selected
}) => {
  const { updateNodeData } = useReactFlow()

  // ✅ CORRECT - Initialize with safe defaults, sync from collaborative state
  const [researchTopic, setResearchTopic] = useState('')
  const [statusMessage, setStatusMessage] = useState('')

  // New state for editing queries
  const [editingQueryId, setEditingQueryId] = useState<string | null>(null)
  const [editingQueryText, setEditingQueryText] = useState('')

  // Collaborative state from data
  const suggestedQueries = data.suggestedQueries || []
  const researchList = data.researchList || []
  const isProcessing = !!data.documentRequest?.processing
  // ✅ CORRECT - Sync from collaborative state to local state
  useEffect(() => {
    setResearchTopic(data.researchTopic || '')
    setStatusMessage(data.statusMessage || '')
  }, [data.researchTopic, data.statusMessage])

  // Clear editing state if the query being edited is no longer available or has changed status
  useEffect(() => {
    if (editingQueryId) {
      const currentQuery = suggestedQueries.find(q => q.id === editingQueryId)

      // Clear editing if:
      // - Query no longer exists
      // - Query status changed (e.g., became disabled, searching, etc.)
      // - Query became disabled
      if (!currentQuery || currentQuery.disabled || currentQuery.status === 'searching') {
        console.log('🧹 Clearing editing state due to query state change:', {
          queryId: editingQueryId,
          queryExists: !!currentQuery,
          queryDisabled: currentQuery?.disabled,
          queryStatus: currentQuery?.status
        })
        setEditingQueryId(null)
        setEditingQueryText('')
      }
    }
  }, [suggestedQueries, editingQueryId])

  // Helper to sync to collaborative state
  const syncNodeData = (updates: Partial<NodeResearcherData>) => {
    updateNodeData(id, { ...data, ...updates })
  }

  const handleStartResearch = () => {
    if (!researchTopic.trim()) return

    setStatusMessage('Generating search queries...')

    // ✅ CORRECT - Only sync to collaborative state on user action
    syncNodeData({
      researchTopic: researchTopic.trim(),
      statusMessage: 'Generating search queries...',
      documentRequest: {
        'sysagent-cmd': 'Generate-Search-Queries',
        processing: true,
        timestamp: Date.now(),
        researchTopic: researchTopic.trim()
      }
    })
  }

  const handleCreateExtract = (researchItemId: string) => {
    const researchItem = researchList.find(item => item.id === researchItemId)
    if (!researchItem) return

    setStatusMessage('Creating web extract node...')

    // ✅ CORRECT - Only sync to collaborative state on user action
    syncNodeData({
      statusMessage: 'Creating web extract node...',
      documentRequest: {
        'sysagent-cmd': 'Create-WebExtract-From-Citations',
        processing: true,
        timestamp: Date.now(),
        researchItemId
      }
    })
  }

  const handleReExtract = (researchItemId: string) => {
    const researchItem = researchList.find(item => item.id === researchItemId)
    if (!researchItem) return

    setStatusMessage('Re-creating web extract nodes...')

    // Reset the research item status to allow re-extraction
    const updatedResearchList = researchList.map(item =>
      item.id === researchItemId
        ? { ...item, status: 'completed' as const, webExtractNodeIds: undefined, webExtractNodeId: undefined, documentNodeId: undefined }
        : item
    )

    syncNodeData({
      researchList: updatedResearchList,
      statusMessage: 'Re-creating web extract nodes...',
      documentRequest: {
        'sysagent-cmd': 'Create-WebExtract-From-Citations',
        processing: true,
        timestamp: Date.now(),
        researchItemId
      }
    })
  }
  const handleReCreateReport = (researchItemId: string) => {
    const researchItem = researchList.find(item => item.id === researchItemId)
    if (!researchItem || !researchItem.webExtractNodeIds) return

    setStatusMessage('Re-creating research report...')

    // Reset the research item status to allow re-creation
    const updatedResearchList = researchList.map(item =>
      item.id === researchItemId
        ? { ...item, status: 'creating_report' as const, documentNodeId: undefined }
        : item
    )

    syncNodeData({
      researchList: updatedResearchList,
      statusMessage: 'Re-creating research report...',
      documentRequest: {
        'sysagent-cmd': 'Create-Research-Report',
        processing: true,
        timestamp: Date.now(),
        researchItemId
      }
    })
  }

  const handleExampleClick = (example: string) => {
    setResearchTopic(example)
  }

  const handleManualMonitor = () => {
    setStatusMessage('Manually checking for completed searches...')

    syncNodeData({
      statusMessage: 'Manually checking for completed searches...',
      documentRequest: {
        'sysagent-cmd': 'Monitor-WebSearch-Results',
        processing: true,
        timestamp: Date.now(),
        autoTriggered: true
      }
    })
  }
  const handleManualReset = () => {
    console.log('🔧 MANUAL RESET: Clearing processing states and commands')
    syncNodeData({
      documentRequest: undefined,
      statusMessage: 'Processing state manually cleared'
    })
  }

  // Query editing functions
  const handleQueryClick = (query: WebSearchQuery) => {
    if (query.disabled) return

    // Start editing mode
    setEditingQueryId(query.id)
    setEditingQueryText(query.query)
  }

  const handleQueryEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingQueryText(e.target.value)
  }

  const handleQueryEditSave = (queryId: string) => {
    if (!editingQueryText.trim()) return

    // Update the query in the suggested queries
    const updatedQueries = suggestedQueries.map(q =>
      q.id === queryId ? { ...q, query: editingQueryText.trim() } : q
    )

    syncNodeData({
      suggestedQueries: updatedQueries
    })

    // Exit editing mode
    setEditingQueryId(null)
    setEditingQueryText('')
  }
  const handleQueryEditCancel = () => {
    setEditingQueryId(null)
    setEditingQueryText('')
  }

  const handleProceedToSearch = (queryId: string) => {
    console.log('🔍 handleProceedToSearch called for queryId:', queryId)
    console.log('🔍 Current editingQueryId:', editingQueryId)

    const query = suggestedQueries.find(q => q.id === queryId)
    if (!query) return

    // Get the final query text (edited or original)
    const finalQuery = editingQueryId === queryId ? editingQueryText.trim() : query.query
    console.log('🔍 Final query to use:', finalQuery)

    // Exit editing mode FIRST before syncing data
    if (editingQueryId === queryId) {
      console.log('🔍 Clearing editing state...')
      setEditingQueryId(null)
      setEditingQueryText('')
    }

    setStatusMessage('Creating web search node...')

    // Small delay to ensure state updates are applied
    setTimeout(() => {
      syncNodeData({
        statusMessage: 'Creating web search node...',
        documentRequest: {
          'sysagent-cmd': 'Create-WebSearch-Node',
          processing: true,
          timestamp: Date.now(),
          selectedQuery: finalQuery,
          queryId
        }
      })
    }, 1000)
  }

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={true}
      selected={selected}
      minWidth={350}
      minHeight={300}
      nodeTitle={data.label || 'AI Researcher'}
      isLocked={!!data.isLocked}
      isMinimized={!!data.isMinimized}
      assignedEmails={
        data.assignedEmails ? data.assignedEmails.split(/\s+/).filter(Boolean) : []
      }
      onAssignUsersClick={() => {
        const emailsStr = window.prompt(
          'Enter emails separated by space',
          data.assignedEmails || ''
        )
        if (emailsStr !== null) {
          syncNodeData({ assignedEmails: emailsStr })
        }
      }}
      status={isProcessing ? 'loading' : undefined}
      className="w-auto h-auto"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Research Topic Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium">What would you like to research?</label>
          <div className="flex gap-2">
            <Input
              value={researchTopic}
              onChange={(e) => setResearchTopic(e.target.value)}
              placeholder="Enter your research topic..."
              disabled={isProcessing}
              className="nodrag"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isProcessing) {
                  handleStartResearch()
                }
              }}
            />
            <Button
              onClick={handleStartResearch}
              disabled={!researchTopic.trim() || isProcessing}
              size="sm"
            >
              {isProcessing ? (
                <>
                  <Brain className="w-4 h-4 mr-2 animate-spin" />
                  Processing
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Research
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Examples */}
        {!researchTopic && !isProcessing && (
          <div className="space-y-2">
            <label className="text-sm text-gray-600">Try these examples:</label>
            <div className="flex flex-wrap gap-2">
              {RESEARCH_EXAMPLES.map((example, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="cursor-pointer hover:bg-blue-50 text-xs"
                  onClick={() => handleExampleClick(example)}
                >
                  {example}
                </Badge>
              ))}
            </div>
          </div>
        )}        {/* Status Message */}
        {statusMessage && (
          <div className="flex items-center justify-between gap-2 p-2 bg-blue-50 rounded text-sm">
            <div className="flex items-center gap-2">
              {isProcessing ? (
                <Brain className="w-4 h-4 animate-spin text-blue-500" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
              {statusMessage}
            </div>
            {isProcessing && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleManualReset}
                className="h-6 text-xs ml-2"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
        )}{/* Suggested Queries */}
        {suggestedQueries.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Select a search query to create web search node:</label>
            <div className="space-y-2">              {suggestedQueries.map((query) => {
              // Determine color based on status
              let bgColor = 'hover:bg-gray-50'; // Default for unselected
              let borderColor = 'border-gray-200';
              let textColor = '';

              if (query.disabled) {
                if (query.status === 'completed') {
                  bgColor = 'bg-green-50 hover:bg-green-100';
                  borderColor = 'border-green-200';
                  textColor = 'text-green-700';
                } else if (query.status === 'searching') {
                  bgColor = 'bg-yellow-50 hover:bg-yellow-100';
                  borderColor = 'border-yellow-200';
                  textColor = 'text-yellow-700';
                } else if (query.status === 'timeout' || query.status === 'error') {
                  bgColor = 'bg-red-50 hover:bg-red-100';
                  borderColor = 'border-red-200';
                  textColor = 'text-red-700';
                } else {
                  bgColor = 'bg-gray-100';
                  borderColor = 'border-gray-300';
                  textColor = 'text-gray-500';
                }
              }

              return (
                <div
                  key={query.id}
                  className={`p-3 border rounded ${bgColor} ${borderColor} ${query.disabled
                      ? 'opacity-90 cursor-not-allowed'
                      : 'cursor-pointer'
                    }`}
                  onClick={() => !query.disabled && handleQueryClick(query)}
                >
                  <div className="flex items-start gap-2">
                    <Search className={`w-4 h-4 mt-0.5 ${query.disabled
                        ? query.status === 'completed' ? 'text-green-500'
                          : query.status === 'searching' ? 'text-yellow-500'
                            : query.status === 'timeout' || query.status === 'error' ? 'text-red-500'
                              : 'text-gray-400'
                        : 'text-blue-500'
                      }`} />                      <div className="flex-1">
                      {editingQueryId === query.id ? (
                        // Edit mode
                        <div className="space-y-2">
                          <Input
                            value={editingQueryText}
                            onChange={handleQueryEdit}
                            placeholder="Edit search query..."
                            className="text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleQueryEditSave(query.id)
                              } else if (e.key === 'Escape') {
                                handleQueryEditCancel()
                              }
                            }}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleProceedToSearch(query.id)}
                              className="h-7 text-xs"
                              disabled={!editingQueryText.trim()}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Proceed to Search
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleQueryEditCancel}
                              className="h-7 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Display mode
                        <div>
                          <div className={`font-medium text-sm ${query.disabled ? textColor : ''}`}>
                            {query.query}
                          </div>
                          <div className={`text-xs mt-1 ${query.disabled ? textColor : 'text-gray-600'}`}>
                            {query.reason}
                          </div>
                          {!query.disabled && (
                            <div className="text-xs text-blue-600 mt-1">
                              Click to edit query before searching
                            </div>
                          )}
                        </div>
                      )}

                      {query.disabled && query.status && (
                        <div className={`text-xs mt-1 font-medium ${textColor}`}>
                          Status: {query.status === 'searching' ? 'Searching...' : query.status === 'completed' ? 'Search completed' : query.status === 'timeout' ? 'Search timed out' : query.status === 'error' ? 'Search failed' : query.status}
                          {query.nodeId && (
                            <div className="text-xs opacity-70 mt-1">
                              Node: {query.nodeId} • {query.label || 'Web Search'}
                            </div>
                          )}
                          {(query.status === 'timeout' || query.status === 'error') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2 h-6 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Implement restart search functionality
                                console.log('Restart search for:', query.query);
                              }}
                            >
                              Restart Search
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}        {/* Research List */}
        {researchList.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Research Progress:</label>
            <div className="space-y-2">
              {researchList.map((item) => {
                // Determine status color and background
                let bgColor = 'bg-blue-50'
                let textColor = 'text-blue-800'
                let borderColor = 'border-blue-200'

                if (item.status === 'completed') {
                  bgColor = 'bg-green-50'
                  textColor = 'text-green-800'
                  borderColor = 'border-green-200'
                } else if (item.status === 'extracting' || item.status === 'creating_report') {
                  bgColor = 'bg-yellow-50'
                  textColor = 'text-yellow-800'
                  borderColor = 'border-yellow-200'
                } else if (item.status === 'report_completed') {
                  bgColor = 'bg-emerald-50'
                  textColor = 'text-emerald-800'
                  borderColor = 'border-emerald-200'
                } else if (item.status === 'report_error' || item.status === 'extraction_timeout') {
                  bgColor = 'bg-red-50'
                  textColor = 'text-red-800'
                  borderColor = 'border-red-200'
                }

                return (
                  <div key={item.id} className={`p-3 border rounded ${bgColor} ${borderColor}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${textColor}`}>{item.query}</div>
                        <div className={`text-xs mt-1 ${textColor.replace('800', '600')}`}>
                          Started: {new Date(item.startedAt).toLocaleString()}
                        </div>
                        {item.completedAt && (
                          <div className={`text-xs mt-1 ${textColor.replace('800', '600')}`}>
                            Completed: {new Date(item.completedAt).toLocaleString()}
                          </div>
                        )}
                        {item.status === 'completed' && item.citationCount && (
                          <div className={`text-xs mt-1 ${textColor.replace('800', '600')}`}>
                            Found {item.citationCount} sources
                          </div>
                        )}
                        {item.extractionProgress && (
                          <div className={`text-xs mt-1 ${textColor.replace('800', '600')}`}>
                            Extraction: {item.extractionProgress}
                          </div>
                        )}
                        <div className={`text-xs mt-1 ${textColor.replace('800', '600')}`}>
                          Status: <span className="font-medium">
                            {item.status === 'searching' && 'Searching...'}
                            {item.status === 'completed' && 'Search completed'}
                            {item.status === 'extracting' && 'Extracting content...'}
                            {item.status === 'creating_report' && 'Creating research report...'}
                            {item.status === 'report_completed' && 'Research report completed'}
                            {item.status === 'report_error' && 'Report creation failed'}
                            {item.status === 'extraction_timeout' && 'Extraction timed out'}
                          </span>
                        </div>
                        {item.error && (
                          <div className="text-xs mt-1 text-red-600">
                            Error: {item.error}
                          </div>
                        )}
                      </div>

                      {/* Initial Extract Button */}
                      {item.status === 'completed' && item.citations && item.citations.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCreateExtract(item.id)}
                          disabled={isProcessing}
                          className="text-xs"
                        >
                          Extract Content
                        </Button>
                      )}
                    </div>

                    {/* Re-action Buttons */}
                    {(item.status === 'report_completed' || item.status === 'report_error' || item.status === 'extraction_timeout') && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReExtract(item.id)}
                          disabled={isProcessing}
                          className="text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Re-Extract
                        </Button>
                        {item.webExtractNodeIds && item.webExtractNodeIds.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReCreateReport(item.id)}
                            disabled={isProcessing}
                            className="text-xs"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Re-Create Report
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Manual Monitor Button */}
        {suggestedQueries.some(q => q.disabled && q.status === 'searching') && !isProcessing && (
          <div className="space-y-2">
            <Button
              onClick={handleManualMonitor}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Search className="w-4 h-4 mr-2" />
              Check for Completed Searches
            </Button>
          </div>
        )}        <Button onClick={handleManualReset} variant="outline" className="text-red-600 hover:text-red-700">
          Reset Processing States
        </Button>
      </div>
    </NodeBaseContainer>
  )
}

export default memo(NodeResearcher)
